/**
 * ÉCHELLE DE DESIGN Orgazone — couleurs, rayons, espacements, ombres, typographie, densité.
 *
 * SOURCE DE VÉRITÉ : `apps/sliderz/retail/design/tokens.css` (maquette). Ce fichier en est le portage
 * TypeScript — toute évolution se fait D'ABORD dans tokens.css, puis se reporte ici. Les couleurs
 * oklch() de la maquette sont converties en hex sRGB exact (math OKLab), React Native ne les gérant pas.
 *
 * ⚠️ DÉPLACÉ DE `@orgazone/ui-native` VERS CE PAQUET (2026-08-04) — sans une ligne de changement. Motif :
 * ces valeurs n'ont RIEN de spécifique à une plateforme (ce sont des nombres et des hex), mais elles
 * étaient enfermées dans un paquet React Native, donc inaccessibles au web. Résultat : les trois SPA
 * s'étaient inventé chacune leur vocabulaire (`--ink9`, `--ink100`, `--ink-100` pour la même échelle
 * d'encre) alors que l'échelle existait déjà, écrite et éprouvée.
 * `ui-native` (ex-`ui-kit`) la RÉEXPORTE : aucun import existant ne change.
 *
 * ⚠️ RESTE À NORMALISER : `shadow` et `font` sont encore exprimés en termes REACT NATIVE (`elevation`,
 * `shadowOffset: {width, height}`, `font.sans: undefined` = police système). Le web devra les traduire
 * ou on leur donnera une forme neutre (blur/offset/opacity, comme `TOAST_SHADOW`) le jour où il les
 * consommera. Ce n'est PAS fait ici : ce déplacement ne change aucune valeur, pour rester vérifiable.
 */

// ---- Brand (teal) ----
export const teal = {
  50: '#EFFDF8',
  100: '#D4F7EC',
  200: '#AAEED9',
  300: '#7BE3C5',
  400: '#5DDFC4',
  500: '#2BC9A3',
  600: '#16A585',
  700: '#117A64',
  800: '#0E5B4D',
  /**
   * ⚠️ CRAN AJOUTÉ LE 2026-09-09, ET IL VENAIT DU DESSIN, PAS D'UNE SYMÉTRIE. Le fond de marque
   * pleine page de Sliderz Mobile Partner est un dégradé à trois arrêts dont le dernier est plus
   * sombre que `800` — sans lui, l'écran d'enrôlement perdait sa profondeur, ou l'app inventait une
   * couleur dans son coin. La rampe s'arrêtait à 800 parce que rien n'était descendu plus bas ;
   * ce n'était pas une limite décidée.
   */
  900: '#0A453B',
} as const;

// ---- Neutrals chauds (ink) — thème CLAIR ----
export const inkLight = {
  950: '#0C0E10',
  900: '#131618',
  800: '#1D2124',
  700: '#2B3034',
  600: '#4A5158',
  500: '#6C747C',
  400: '#97A0A8',
  300: '#C4CCD2',
  200: '#E2E6EA',
  100: '#EEF1F3',
  50: '#F7F8F9',
} as const;

// ---- Neutrals — thème SOMBRE (échelle inversée, cf. tokens.css [data-theme=dark]) ----
export const inkDark = {
  950: '#FFFFFF',
  900: '#F3F5F7',
  800: '#E9ECEF',
  700: '#D4D9DD',
  600: '#B6BDC4',
  500: '#8F98A1',
  400: '#6B747C',
  300: '#3A4046',
  200: '#262B30',
  100: '#1C2024',
  50: '#15181B',
} as const;

// ---- Accents (oklch maquette → hex sRGB exact) ----
export const accent = {
  amber: '#ECA851', //   oklch(0.78 0.13 70)
  amberBg: '#FFF0D4', // oklch(0.96 0.04 80)
  amberInk: '#753D00', // oklch(0.42 0.12 70) — texte sur amberBg (.s-pill-amber)
  amberIcon: '#A45F00', // oklch(0.55 0.14 70) — icônes sur amberBg
  rose: '#EB8186', //    oklch(0.72 0.13 18)
  roseBg: '#FFEAE9', //  oklch(0.96 0.03 20)
  roseInk: '#90101A', // oklch(0.42 0.16 25) — texte sur roseBg (.s-pill-rose)
  violet: '#A495F0', //  oklch(0.72 0.13 290)
  violetBg: '#F1EFFF', // oklch(0.96 0.03 290)
  blue: '#4BAEED', //    oklch(0.72 0.13 240)
  blueBg: '#E1F5FF', //  oklch(0.96 0.03 240)
  /**
   * VERT — pastille « au-dessus de l'attendu » (fond de caisse supérieur au fond visé, §9.2).
   * ⚠️ Distinct du teal de MARQUE, délibérément : le teal signale l'application, ce vert signale un
   *    CONSTAT chiffré. Les confondre ferait lire une pastille de statut comme un élément d'interface.
   *    Ces deux valeurs vivaient en dur dans `OpeningScreen`, sans nom — donc introuvables.
   */
  greenBg: '#E2F1D5', // oklch(0.94 0.04 130)
  greenInk: '#315300', // texte sur greenBg
} as const;

// ---- SUR une surface de couleur, et VOILES ----
/**
 * Ce qu'on pose SUR une surface colorée, et ce qu'on pose PAR-DESSUS l'écran.
 *
 * ⚠️ CES JETONS MANQUAIENT, ET LEUR ABSENCE SE MESURAIT (audit 2026-08-26) : ~20 `#FFFFFF` écrits à
 * la main sur des fonds teal, et **quatre** voiles de modale différents pour le même geste. Le vrai
 * défaut n'était pas l'écriture en dur — c'était qu'aucune valeur ne faisait autorité, donc qu'elles
 * ne s'accordaient pas.
 * ⚠️ `onAccent` NE SUIT PAS LE THÈME, délibérément : la surface reste colorée en mode sombre, donc
 * le texte qui la couvre garde le même contraste. L'inverser le rendrait illisible.
 */
export const surface = {
  /** Texte / icône sur une surface de couleur (bouton actif, pastille, bandeau de marque). */
  onAccent: '#FFFFFF',
  /** Voile d'une modale — la valeur de la primitive Modal du shell (`design/shell.jsx`). */
  scrim: 'rgba(15,20,25,0.5)',
  /** Voile RENFORCÉ des écrans qui BLOQUENT (hors ligne, verrouillage) : on ne clique pas derrière. */
  scrimStrong: 'rgba(15,20,25,0.72)',
  /** Teintes posées sur le bandeau de MARQUE (dégradé teal) : appairage, connexion, onboarding. */
  /**
   * Sous-titre sur le bandeau de marque. ⚠️ **0,90 ET PAS 0,85** — mesuré, pas choisi : sur le
   * teal-700 du bandeau, 0,85 donne un contraste de **4,27**, sous le seuil WCAG AA de 4,5 ; 0,90
   * donne 4,60. Les deux écrans de marque divergeaient (0,90 à l'appairage, 0,85 à la connexion) et
   * une première passe les a « alignés » sur la valeur BASSE : c'était uniformiser vers la faute.
   */
  onBrandDim: 'rgba(255,255,255,0.90)',
  onBrandFaint: 'rgba(255,255,255,0.70)',
  onBrandVeil: 'rgba(255,255,255,0.16)',
  /**
   * Posé SUR UNE PHOTO (pastille de code article, bouton de retrait d'une vignette). Ni le thème ni
   * l'échelle d'encre ne conviennent : le fond est une image, dont on ne sait rien. Il faut donc un
   * voile OPAQUE — ces deux valeurs étaient écrites en dur dans trois écrans, à 0,85 et 0,90.
   */
  onVisualLight: 'rgba(255,255,255,0.85)',
  onVisualDark: 'rgba(15,20,25,0.72)',
} as const;

/**
 * LE PANNEAU DE MARQUE de la caisse — connexion et mise en service (« proposition C » de la maquette) : le
 * noir du rail, une lueur teal sourde en haut à gauche, un reflet froid en bas à droite.
 *
 * ⚠️ HORS THÈME : le rail s'INVERSE en sombre (fond clair, texte foncé),
 *    donc lire `sidebarBg`/`sidebarText` du thème rendait le panneau clair et son « Sliderz » blanc
 *    illisible. Ce sont les valeurs du rail CLAIR, figées une fois pour toutes — même raisonnement que
 *    le bandeau `CHROME` du web.
 * Les deux lueurs sont la conversion exacte des `oklch(0.34 0.065 172)` et `oklch(0.26 0.045 200)` de la
 * maquette (`oklchToHex`) : React Native ne lit pas l'oklch.
 */
export const brandPanel = {
  bg: '#0C0E10',
  glowTeal: '#024334',
  glowCool: '#002A2D',
  /** « Sliderz », accroche. */
  title: '#FFFFFF',
  /** « Retail », sous-accroche, paragraphe — le texte du rail clair. */
  text: '#C4CCD2',
  /** Pied de panneau, mentions. */
  textDim: '#97A0A8',
  /** Surtitre (« Mise en service ») — le teal-300 de l'échelle. */
  kicker: '#7BE3C5',
} as const;

// ---- Statuts ----
export const status = {
  success: teal[500],
  warning: '#F0A646', // oklch(0.78 0.14 70)
  danger: '#E64343', //  oklch(0.62 0.20 25)
} as const;

// ---- Rayons ----
export const radius = {
  xs: 6,
  sm: 10,
  md: 14,
  lg: 20,
  xl: 28,
  pill: 999,
} as const;

// ---- Rythme d'espacement ----
export const spacing = {
  gap: 16,
} as const;

// ---- Densité (normal par défaut ; compact/comfortable = variantes Tweaks) ----
export type Density = 'compact' | 'normal' | 'comfortable';
export const density: Record<Density, { rowH: number; pad: number; txt: number }> = {
  compact: { rowH: 36, pad: 12, txt: 14 },
  normal: { rowH: 44, pad: 16, txt: 15 },
  comfortable: { rowH: 52, pad: 20, txt: 16 },
};

// ---- Ombres (équivalents RN des --sh-* ; elevation Android + shadow* iOS) ----
export const shadow = {
  sm: {
    elevation: 1,
    shadowColor: '#0F1419',
    shadowOpacity: 0.04,
    shadowRadius: 2,
    shadowOffset: { width: 0, height: 1 },
  },
  md: {
    elevation: 3,
    shadowColor: '#0F1419',
    shadowOpacity: 0.05,
    shadowRadius: 8,
    shadowOffset: { width: 0, height: 2 },
  },
  lg: {
    elevation: 8,
    shadowColor: '#0F1419',
    shadowOpacity: 0.12,
    shadowRadius: 32,
    shadowOffset: { width: 0, height: 12 },
  },
} as const;

// ---- Typo ----
// La maquette utilise 'Inter Tight' : non bundlée pour l'instant (nécessite un
// rebuild natif — lot prévu avec les autres deps natives). Police système en attendant.
export const font = {
  sans: undefined as string | undefined, // système (SF Pro iOS / Roboto Android)
  mono: 'monospace',
} as const;
